<template>
  <VAppLayout :is-nav-visible="false">
    <div class="welcome">
      <!-- HERO — pinned; the sheet rides up over it on scroll -->
      <header class="welcome__hero" :class="{ 'welcome__hero--no-photo': !comPhoto }">
        <img v-if="comPhoto" :src="comPhoto" alt="" class="welcome__hero-img">
        <div class="welcome__scrim"></div>

        <div class="welcome__topbar">
          <div class="welcome__logo-chip" :class="{ 'welcome__logo-chip--brand': !hasLogo, 'welcome__logo-chip--hidden': comShowBigLogo }">
            <img v-if="hasLogo" :src="logoSrc" alt="Logo" class="welcome__logo-img">
            <svg v-else viewBox="0 0 24 24" width="24" height="24" fill="none">
              <path d="M5 3V10C5 11.1 5.9 12 7 12V21M9 3V8M5 5V8M16 3C14.5 3 14 6 14 9C14 10.5 14.7 11 16 11V21"
                    stroke="#fff" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="welcome__actions">
            <button type="button"
                    class="welcome__share sm-press"
                    :aria-label="isDark ? $t('pages.welcome.lightMode') : $t('pages.welcome.darkMode')"
                    @click="toggleDarkMode">
              <svg v-if="!isDark" width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
              </svg>
              <svg v-else width="19" height="19" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="2"/>
                <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.1 5.1l1.4 1.4M17.5 17.5l1.4 1.4M18.9 5.1l-1.4 1.4M6.5 17.5l-1.4 1.4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
            <button type="button"
                    class="welcome__share sm-press"
                    :aria-label="$t('pages.welcome.share')"
                    @click="shareMenu">
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
                <path d="M12 3v12M12 3L8 7M12 3l4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M5 12v6a2 2 0 002 2h10a2 2 0 002-2v-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <VAppLocale class="welcome__locale" />
          </div>
        </div>

        <div class="welcome__hero-foot">
          <img v-if="comShowBigLogo" :src="logoSrc" :alt="comCompanyName" class="welcome__big-logo">
          <h1 v-else class="welcome__name">{{ comCompanyName }}</h1>
          <p v-if="comTagline" class="welcome__tagline">{{ comTagline }}</p>
        </div>
      </header>

      <!-- Overlapping sheet -->
      <section class="welcome__sheet">
        <div v-if="!locale.hide_welcome_greeting" class="welcome__intro">
          <h2 class="welcome__greeting">{{ $t('pages.welcome.greeting') }}</h2>
          <p class="welcome__subtitle">{{ comSubtitle }}</p>
        </div>

        <VButton v-if="comEnabledNav.has('menu')" variant="primary" gradient size="lg" full-width class="welcome__cta" @click="goToMenu">
          <span>{{ $t('pages.welcome.viewMenu') }}</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M5 12H19M13 6L19 12L13 18" stroke="#fff" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </VButton>

        <div v-if="comTiles.length" class="welcome__tiles">
          <button v-for="tile in comTiles"
                  :key="tile.key"
                  type="button"
                  class="welcome__tile sm-press"
                  @click="tile.onClick">
            <span class="welcome__tile-badge">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                <template v-if="tile.icon === 'receipt'">
                  <path d="M5 2.5a1 1 0 011.6-.8L8 2.8l1.4-1.1a1 1 0 011.2 0L12 2.8l1.4-1.1a1 1 0 011.2 0L16 2.8l1.4-1.1A1 1 0 0119 2.5V20l-1.6-1.2a1 1 0 00-1.2 0L14 20.1l-1.4-1.3a1 1 0 00-1.2 0L10 20.1l-1.4-1.3a1 1 0 00-1.2 0L5 20V2.5ZM8 7.5h8M8 11h8M8 14.5h5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                </template>
                <template v-else-if="tile.icon === 'star'">
                  <path d="M12 17.27l5.18 3.29c.66.42 1.47-.2 1.3-.97l-1.38-5.9 4.58-3.97c.6-.52.28-1.5-.5-1.57l-6.03-.51L12.7 1.94c-.3-.72-1.32-.72-1.62 0L8.72 7.64l-6.03.51c-.78.07-1.1 1.05-.5 1.57l4.58 3.97-1.38 5.9c-.17.77.64 1.39 1.3.97L12 17.27Z" fill="currentColor"/>
                </template>
                <template v-else-if="tile.icon === 'coins'">
                  <ellipse cx="9" cy="6" rx="6" ry="3" stroke="currentColor" stroke-width="1.8"/>
                  <path d="M3 6v5c0 1.66 2.69 3 6 3 .7 0 1.36-.06 1.98-.17M3 11v5c0 1.66 2.69 3 6 3 1 0 1.95-.12 2.78-.34" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                  <circle cx="16.5" cy="14.5" r="5" stroke="currentColor" stroke-width="1.8"/>
                  <path d="M16.5 12.2v4.6M14.5 13.3h2.6a1 1 0 010 2H16a1 1 0 000 2h2.4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </template>
                <template v-else>
                  <path d="M5 12.55a11 11 0 0114.08 0M8.53 16.11a6 6 0 016.95 0M2 9a16 16 0 0120 0" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  <circle cx="12" cy="19.5" r="1.2" fill="currentColor"/>
                </template>
              </svg>
            </span>
            <span class="welcome__tile-text">
              <span class="welcome__tile-label">{{ tile.label }}</span>
              <span class="welcome__tile-sub">{{ tile.sub }}</span>
            </span>
          </button>
        </div>

        <div v-if="comCustomItems.length" class="welcome__find">
          <a v-for="(item, index) in comCustomItems"
             :key="'custom-' + index"
             :href="item.url"
             target="_blank"
             rel="noopener"
             class="welcome__resource sm-press">
            <span class="welcome__resource-logo">
              <img v-if="item.icon" :src="item.icon" alt="" class="welcome__resource-logo-img">
              <template v-else>{{ avatarLetter(item.title) }}</template>
            </span>
            <span class="welcome__resource-text">
              <span class="welcome__resource-label">{{ item.title }}</span>
              <span class="welcome__resource-sub">{{ cleanUrl(item.url) }}</span>
            </span>
            <svg class="welcome__resource-arrow" width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path d="M7 17L17 7M17 7H9M17 7V15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </div>

        <a href="https://myrest.io?utm_source=sm" target="_blank" rel="noopener" class="welcome__powered">
          <span class="welcome__powered-text">{{ $t('pages.welcome.poweredBy') }}</span>
          <img src="/img/myrest-logo.svg" alt="myREST" class="welcome__powered-logo">
        </a>
      </section>
    </div>

    <!-- Wi-Fi bottom sheet (teleported to body so fixed positioning is correct) -->
    <div ref="wifiSheet" v-show="wifiOpen" class="welcome-portal">
      <div class="welcome-portal__backdrop" :class="{ 'is-in': wifiIn }" @click="closeWifi"></div>
      <div class="welcome-portal__sheet"
           :class="{ 'is-in': wifiIn }"
           :style="sheetDragStyle"
           @touchstart="onSheetTouchStart"
           @touchmove="onSheetTouchMove"
           @touchend="onSheetTouchEnd"
           @touchcancel="onSheetTouchEnd">
        <div class="welcome-portal__handle"></div>
        <button type="button"
                class="welcome-portal__close sm-press"
                :aria-label="$t('Close')"
                @click="closeWifi">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
          </svg>
        </button>
        <h3 class="welcome-portal__title">{{ $t('pages.welcome.q.wifi.label') }}</h3>
        <div class="wifi-row" v-if="wifiNetwork">
          <span class="wifi-row__meta">
            <span class="wifi-row__label">{{ $t('pages.welcome.wifi.network') }}</span>
            <span class="wifi-row__value">{{ wifiNetwork }}</span>
          </span>
          <button type="button" class="wifi-row__copy sm-press" @click="copyText(wifiNetwork)" :aria-label="$t('pages.welcome.wifi.network')">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="9" y="9" width="11" height="11" rx="2.5" stroke="currentColor" stroke-width="1.8"/>
              <path d="M5 15V5a2 2 0 012-2h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="wifi-row" v-if="wifiPassword">
          <span class="wifi-row__meta">
            <span class="wifi-row__label">{{ $t('pages.welcome.wifi.password') }}</span>
            <span class="wifi-row__value">{{ wifiPassword }}</span>
          </span>
          <button type="button" class="wifi-row__copy sm-press" @click="copyText(wifiPassword)" :aria-label="$t('pages.welcome.wifi.password')">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="9" y="9" width="11" height="11" rx="2.5" stroke="currentColor" stroke-width="1.8"/>
              <path d="M5 15V5a2 2 0 012-2h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </VAppLayout>
</template>

<script>
import VAppLayout from "@/layouts/VAppLayout.vue";
import VAppLocale from "@/components/ui/VAppLocale.vue";
import VButton from "@/components/ui/VButton.vue";
import { getTheme, toggleTheme } from "@/utils/theme";
import { lockNav, unlockNav } from "@/utils/navLock";

export default {
  name: 'Welcome',
  components: {
    VAppLocale,
    VAppLayout,
    VButton,
  },
  data() {
    return {
      wifiOpen: false,
      wifiIn: false,
      isDark: getTheme() === 'dark',
      sheetDragging: false,
      sheetDragY: 0,
    };
  },
  computed: {
    locale() {
      return this.$store.getters['getCompanyData'].locale;
    },
    comCompanyName() {
      return this.locale.name;
    },
    comPhoto() {
      return this.locale.photo || null;
    },
    comTableNumber() {
      return this.$store.getters['getTableNum'];
    },
    hasLogo() {
      return !!this.locale.logo;
    },
    logoSrc() {
      return this.locale.logo;
    },
    comShowBigLogo() {
      // sm_show_logo_instead_of_name: render the venue logo big, in place of the
      // name — but only when a logo actually exists, otherwise fall back to the
      // name so the hero is never left empty. When on, the small topbar logo is
      // hidden too (it would just duplicate the big one).
      return !!this.locale.sm_show_logo_instead_of_name && this.hasLogo;
    },
    comSubtitle() {
      // Tailor the greeting line to the ordering mode.
      switch (this.locale.smart_menu_mode) {
        case 'wmm': return this.$t('pages.welcome.subtitleWmm');   // view-only, pay POS bill
        case 'hot': return this.$t('pages.welcome.subtitleHotel'); // hotel: order to room
        default:    return this.$t('pages.welcome.subtitle');      // qsr / sot: order from table
      }
    },
    comTagline() {
      const base = (this.locale.menu_welcome_text || '').trim();
      const n = this.comTableNumber;
      const table = n ? `${this.$t('pages.welcome.table')} ${n}` : '';
      if (base && table) return `${base} · ${table}`;
      return base || table;
    },
    cartCount() {
      const cart = this.$store.getters['cartStore/getCart'] || [];
      return cart.reduce((sum, item) => sum + (item.count || 0), 0);
    },
    activeWaiters() {
      return (this.$store.getters['getWaitersList'] || []).filter(w => w.is_active === 1);
    },
    hasWifi() {
      // Only when BOTH the network name and the password are set.
      return !!(this.locale.wifi_ussd && this.locale.wifi_password);
    },
    wifiNetwork() {
      return this.locale.wifi_ussd || '';
    },
    wifiPassword() {
      return this.locale.wifi_password || '';
    },
    comEnabledNav() {
      let navItems = this.locale.nav_items;

      if (this.$store.getters['getTableNum'] === null && !this.$store.getters['getCartEnabled']) {
        navItems = (Object.entries(navItems)).filter(item => item[0] !== 'bill');
        return new Set((navItems).filter(item => item[1]).map(item => item[0]));
      }
      return new Set(Object.entries(navItems).filter(item => item[1]).map(item => item[0]));
    },
    comTiles() {
      const make = (key, icon, onClick) => ({
        key, icon,
        label: this.$t(`pages.welcome.q.${key}.label`),
        sub: this.$t(`pages.welcome.q.${key}.sub`),
        onClick,
      });

      // Standard tiles mirror the bottom-nav toggles (locale.nav_items): a tile
      // shows only when its nav item is enabled AND it has something to show
      // (bill needs a cart, tip needs an active waiter). Wi-Fi isn't a nav item,
      // so it's gated solely by being configured.
      const tiles = [];
      if (this.comEnabledNav.has('bill') && this.cartCount > 0) tiles.push(make('bill', 'receipt', () => this.$router.push('/bill')));
      if (this.comEnabledNav.has('review')) tiles.push(make('review', 'star', () => this.$router.push('/review')));
      if (this.comEnabledNav.has('tip') && this.activeWaiters.length > 0) tiles.push(make('tip', 'coins', () => this.$router.push('/tip')));
      if (this.hasWifi) tiles.push(make('wifi', 'wifi', this.openWifi));
      return tiles;
    },
    comCustomItems() {
      return this.locale.nav_items_custom || [];
    },
    sheetDragStyle() {
      // While a finger drags the sheet, pin it to the finger with no transition;
      // once released the inline style drops away and the class transition takes
      // over (back to open, or down past the edge via closeWifi).
      if (!this.sheetDragging) return null;
      return { transform: `translateY(${this.sheetDragY}px)`, transition: 'none' };
    },
  },
  mounted() {
    // Teleport the Wi-Fi sheet to <body> so its fixed positioning ignores any
    // transformed / backdrop-filtered ancestor.
    if (this.$refs.wifiSheet) {
      document.body.appendChild(this.$refs.wifiSheet);
    }
  },
  beforeDestroy() {
    if (this.$refs.wifiSheet && this.$refs.wifiSheet.parentNode) {
      this.$refs.wifiSheet.parentNode.removeChild(this.$refs.wifiSheet);
    }
  },
  methods: {
    goToMenu() {
      this.$router.push('/menu');
    },
    toggleDarkMode() {
      this.isDark = toggleTheme() === 'dark';
    },
    async shareMenu() {
      // Share a clean venue link (root + ?cn=<code>), not the current table-bound
      // URL, so the recipient lands on the venue's smart menu.
      const code = this.$store.getters.getCompanyName;
      const url = code
        ? `${window.location.origin}/?cn=${encodeURIComponent(code)}`
        : window.location.href;
      // Only title + url. Don't pass `text`: the OS share sheet appends it to the
      // copied/shared link (e.g. "<url> <venue name>"), polluting the clean URL.
      const shareData = { title: this.comCompanyName, url };

      // Native share sheet on phones; fall back to copying the link on desktop /
      // unsupported browsers.
      if (navigator.share) {
        try {
          await navigator.share(shareData);
        } catch (e) {
          // user dismissed the sheet (AbortError) or it failed — nothing to do
        }
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(url);
          this.$toast.success(this.$t('pages.welcome.shareCopied'));
        } catch (e) {
          // clipboard blocked — ignore
        }
      }
    },
    openWifi() {
      this.wifiOpen = true;
      lockNav();
      this.$nextTick(() => {
        window.requestAnimationFrame(() => { this.wifiIn = true; });
      });
    },
    closeWifi() {
      this.wifiIn = false;
      unlockNav();
      setTimeout(() => { this.wifiOpen = false; }, 280);
    },
    onSheetTouchStart(e) {
      if (!this.wifiIn) return;
      const t = e.touches[0];
      this._sheetStartY = t.clientY;
      this._sheetLastY = t.clientY;
      this._sheetLastT = e.timeStamp;
      this._sheetVelocity = 0;
      this.sheetDragging = true;
      this.sheetDragY = 0;
    },
    onSheetTouchMove(e) {
      if (!this.sheetDragging) return;
      const t = e.touches[0];
      const delta = t.clientY - this._sheetStartY;
      // Follow the finger downward only — the sheet has nowhere to go up.
      this.sheetDragY = delta > 0 ? delta : 0;
      const dt = e.timeStamp - this._sheetLastT;
      if (dt > 0) this._sheetVelocity = (t.clientY - this._sheetLastY) / dt;
      this._sheetLastY = t.clientY;
      this._sheetLastT = e.timeStamp;
      if (delta > 0) e.preventDefault();
    },
    onSheetTouchEnd() {
      if (!this.sheetDragging) return;
      this.sheetDragging = false;
      // Close on a long pull or a quick downward flick; otherwise spring back.
      const shouldClose = this.sheetDragY > 96
        || (this.sheetDragY > 24 && this._sheetVelocity > 0.45);
      this.sheetDragY = 0;
      if (shouldClose) this.closeWifi();
    },
    copyText(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(() => {});
      }
      this.$toast.success(this.$t('pages.welcome.wifi.copied'));
    },
    avatarLetter(title) {
      return (title || '?').trim().charAt(0).toUpperCase();
    },
    cleanUrl(url) {
      return (url || '')
          .replace(/^https?:\/\//, '')
          .replace(/^www\./, '')
          .replace(/\/$/, '');
    },
  }
};
</script>

<style lang="scss" scoped>
.welcome {
  /* viewport units, not %: the document scrolls, ancestor heights are content-driven */
  min-height: 100vh;
  min-height: 100svh;
  /* Card colour, not the page tint: on tall screens the area below the sheet
     would otherwise show a grey strip under the white card. The hero covers the
     top with its own background, so this only paints below the sheet. */
  background: var(--surface-card);
}

/* ---------- HERO ---------- */
.welcome__hero {
  position: sticky;
  top: 0;
  z-index: 0;
  height: 384px;
  overflow: hidden;
  /* Neutral dark under the photo: with the document-scrolling sticky hero, iOS
     Safari's backdrop-filter on the topbar buttons can sample the hero's own
     background instead of the photo — a brand-coloured layer here tinted the
     frosted buttons red. Venues without a photo keep the brand gradient (below),
     where that tint is the intended look anyway. Matches the scrim base colour
     so Safari's derived status-bar strip blends with the vignette seamlessly. */
  background: #0C0A12;

  &--no-photo {
    background: var(--grad-brand);   /* fallback when no venue photo — follows the venue brand colour */
  }
}

.welcome__hero-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.welcome__scrim {
  position: absolute;
  inset: 0;
  pointer-events: none;
  /* The very top fades from near-solid dark: Safari paints the status-bar strip
     (which no web page can draw into — safe-area-inset-top is 0 in browser mode)
     with a colour derived from the page. Starting the vignette almost opaque
     makes that OS strip and the photo read as one continuous dark fade instead
     of a visible seam above the image. */
  background: linear-gradient(
    180deg,
    rgba(12, 10, 18, 0.92) 0%,
    rgba(12, 10, 18, 0.45) 12%,
    rgba(12, 10, 18, 0) 36%,
    rgba(12, 10, 18, 0.62) 100%
  );
}

.welcome__topbar {
  position: absolute;
  top: calc(54px + var(--safe-top, 0px));
  left: 22px;
  right: 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.welcome__logo-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  overflow: hidden;
  background: var(--surface-card);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.24);
  flex-shrink: 0;

  &--brand {
    background: var(--grad-brand);
  }

  /* Kept in the flow (so the action buttons stay right-aligned) but invisible
     when the big logo replaces the name. */
  &--hidden {
    visibility: hidden;
  }
}

.welcome__logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 6px;
  box-sizing: border-box;
}

.welcome__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.welcome__share {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.34);
  border-radius: var(--radius-pill);
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: #fff;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.welcome__locale {
  ::v-deep .locale__pill {
    height: 40px;
    padding: 0 14px;
    gap: 7px;
    border: 1px solid rgba(255, 255, 255, 0.34);
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    color: #fff;
  }

  ::v-deep .locale__code { color: #fff; }
  ::v-deep .locale__caret { opacity: 0.85; }
  ::v-deep .locale__caret path { fill: #fff; }
}

.welcome__hero-foot {
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 48px;   /* extra breathing room above the sheet */
}

.welcome__name {
  margin: 0;
  font-size: 40px;
  font-weight: var(--weight-black);
  line-height: 1.0;
  letter-spacing: -0.025em;
  color: #fff;
  text-shadow: 0 4px 28px rgba(0, 0, 0, 0.4);
}

.welcome__big-logo {
  display: block;
  max-width: 78%;
  max-height: 120px;
  width: auto;
  height: auto;
  object-fit: contain;
  /* Mirror the name's text-shadow so the logo stays legible over any photo. */
  filter: drop-shadow(0 4px 28px rgba(0, 0, 0, 0.4));
}

.welcome__tagline {
  margin: 9px 0 0;
  font-size: 15px;
  font-weight: var(--weight-semibold);
  color: rgba(255, 255, 255, 0.92);
}

/* ---------- SHEET ---------- */
.welcome__sheet {
  position: relative;
  z-index: 1;
  margin-top: -26px;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px 20px calc(28px + var(--safe-bottom, 0px));
  background: var(--surface-card);
  border-radius: 28px 28px 0 0;
  box-shadow: 0 -16px 40px rgba(20, 16, 26, 0.16);
}

.welcome__greeting {
  margin: 0;
  font-size: 23px;
  font-weight: var(--weight-bold);
  letter-spacing: -0.01em;
  color: var(--text-primary);
}

.welcome__subtitle {
  margin: 5px 0 0;
  font-size: 14px;
  font-weight: var(--weight-medium);
  line-height: 1.4;
  color: var(--text-secondary);
}

.welcome__cta {
  span { font-size: var(--text-md); }
}

/* ---------- TILES ---------- */
.welcome__tiles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.welcome__tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--border-subtle);
  border-radius: 22px;
  background: var(--surface-card);
  box-shadow: 0 4px 16px rgba(74, 55, 73, 0.05);
  cursor: pointer;
  text-align: left;
  font-family: var(--font-sans);
  -webkit-tap-highlight-color: transparent;
}

.welcome__tile-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: var(--surface-subtle);
  color: var(--text-secondary);
}

.welcome__tile-text {
  min-width: 0;
}

.welcome__tile-label {
  display: block;
  font-size: 15.5px;
  font-weight: var(--weight-bold);
  color: var(--text-primary);
}

.welcome__tile-sub {
  display: block;
  margin-top: 3px;
  font-size: 12.5px;
  font-weight: var(--weight-medium);
  line-height: 1.3;
  color: var(--text-muted);
}

/* ---------- FIND US (custom links) ---------- */
.welcome__find {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.welcome__resource {
  display: flex;
  align-items: center;
  gap: 13px;
  width: 100%;
  padding: 11px 14px;
  border: 1px solid var(--border-subtle);
  border-radius: 16px;
  background: var(--surface-card);
  text-decoration: none;
  font-family: var(--font-sans);
  -webkit-tap-highlight-color: transparent;
}

.welcome__resource-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 12px;
  background: var(--surface-subtle);
  border: 1px solid var(--border-subtle);
  color: var(--text-muted);
  font-size: 16px;
  font-weight: var(--weight-black);
  overflow: hidden;
}

.welcome__resource-logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 11px;
}

.welcome__resource-text {
  flex: 1;
  min-width: 0;
}

.welcome__resource-label {
  display: block;
  font-size: 15px;
  font-weight: var(--weight-bold);
  color: var(--text-primary);
}

.welcome__resource-sub {
  display: block;
  margin-top: 1px;
  font-size: 12.5px;
  font-weight: var(--weight-medium);
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.welcome__resource-arrow {
  flex-shrink: 0;
  color: var(--text-muted);
}

/* ---------- POWERED BY ---------- */
.welcome__powered {
  margin-top: auto;
  padding-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
}

.welcome__powered-text {
  font-size: 12px;
  font-weight: var(--weight-semibold);
  color: var(--text-muted);
}

.welcome__powered-logo {
  height: 17px;
  width: auto;
  display: block;
}

/* shared press affordance */
.sm-press {
  transition: transform var(--dur-fast) var(--ease-standard),
              box-shadow var(--dur-base) var(--ease-standard);

  &:active {
    transform: scale(0.97);
    box-shadow: var(--shadow-sm);
  }
}

/* ---------- Wi-Fi bottom sheet ---------- */
.welcome-portal {
  position: fixed;
  inset: 0;
  z-index: 1000;
}

.welcome-portal__backdrop {
  position: absolute;
  inset: 0;
  background: var(--overlay-scrim);
  opacity: 0;
  transition: opacity 280ms var(--ease-standard);

  &.is-in { opacity: 1; }
}

.welcome-portal__sheet {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 12px 20px calc(28px + var(--safe-bottom, 0px));
  background: var(--surface-card);
  border-radius: var(--radius-sheet) var(--radius-sheet) 0 0;
  box-shadow: var(--shadow-xl);
  transform: translateY(100%);
  transition: transform 320ms var(--ease-out);
  /* Nothing inside scrolls — hand every touch gesture to the drag-to-close JS. */
  touch-action: none;

  &.is-in { transform: translateY(0); }
}

.welcome-portal__handle {
  width: 42px;
  height: 5px;
  margin: 0 auto 14px;
  border-radius: var(--radius-pill);
  background: var(--border-default);
}

.welcome-portal__close {
  position: absolute;
  top: 14px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--radius-pill);
  background: var(--surface-subtle);
  color: var(--text-secondary);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.welcome-portal__title {
  margin: 0 0 14px;
  font-size: var(--text-xl);
  font-weight: var(--weight-black);
  color: var(--text-primary);
}

.wifi-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 14px;
  border-radius: var(--radius-2xl);
  background: var(--surface-subtle);

  & + & { margin-top: 9px; }
}

.wifi-row__meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.wifi-row__label {
  font-size: 12px;
  font-weight: var(--weight-bold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.wifi-row__value {
  margin-top: 2px;
  font-size: var(--text-md);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
  word-break: break-all;
}

.wifi-row__copy {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border: none;
  border-radius: 13px;
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-size: 18px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
</style>
